import VisibilityIcon from "@mui/icons-material/Visibility"
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff"
import { Typography } from "@mui/material"
import { useCallback, useMemo } from "react"
import {
  createSearchParams,
  generatePath,
  useMatch,
  useNavigate,
} from "react-router"
import { useSearchParams } from "../../../../hooks/useSearchParams"
import { ROUTES, stringifySearchParams } from "../../../../utils/constants"
import {
  getLocationId,
  getLocationNameFromMapboxLocation,
} from "../../../../utils/helpers"
import { buildAssetFromLocationDetails } from "../../../../utils/helpers/assetHelper"
import { trackEvent } from "../../../../utils/mixpanel"
import { useCreateAsset } from "../../../WatchedLocations/AssetManagement/hooks/useCreateAsset"
import { useRemoveAsset } from "../../../WatchedLocations/AssetManagement/hooks/useRemoveAsset"
import { useListAssets } from "../../../WatchedLocations/hooks/useAssetService"
import { LOCATION_DETAILS_TABS } from "../../LocationDetailsTabs"
import { ActionMenuItem, IconStyles } from "./ActionsShared"

export const WatchAssetMenuItem: React.FC = () => {
  const navigate = useNavigate()
  const { id, title, subtitle, centroid } = useSearchParams()
  const { data: assetsData } = useListAssets({
    includeGlobal: false,
  })
  const { mutateAsync } = useRemoveAsset()
  const assetMatch = useMatch(ROUTES.ASSET)
  const asset_id = assetMatch?.params.asset_id
  const { mutateAsync: create } = useCreateAsset()

  const saved = useMemo(() => {
    if (asset_id)
      // if this is an asset route, look for matching asset id
      return assetsData.assets.find(asset => asset.asset_id === asset_id)

    // If it's not on an asset route yet but we've saved it, look for assets with matching location id.
    // This won't be necessary once we're returning the asset id on the create response.
    return assetsData.assets.find(asset => getLocationId(asset) === id)
  }, [asset_id, assetsData.assets, id])

  const createAsset = useCallback(async () => {
    const locationId = id
    if (!locationId) return
    const newAsset = buildAssetFromLocationDetails({
      locationId,
      title,
      subtitle,
      centroid,
    })
    const created = await create({ asset: newAsset })
    trackEvent("MANAGE_LOCATION", {
      action: "add",
      locationId: getLocationId(newAsset),
      industry: newAsset.industry || "",
    })

    if (!created?.asset_id) return

    void navigate(
      {
        pathname: generatePath(ROUTES.ASSET, {
          asset_id: created.asset_id,
        }),
        search: createSearchParams({
          tab: LOCATION_DETAILS_TABS[0].path,
        }).toString(),
      },
      {
        replace: true,
      }
    )
  }, [id, title, subtitle, centroid, create, navigate])

  const removeAsset = useCallback(async () => {
    if (!saved) return

    const location_id = getLocationId(saved)
    const { title: locationTitle, subtitle: locationSubtitle } =
      getLocationNameFromMapboxLocation(
        saved.base_event?.mapbox_location ?? undefined,
        location_id
      )

    if (!saved.asset_id) return
    await mutateAsync({ asset_id: saved.asset_id }).catch(console.error)
    trackEvent("MANAGE_LOCATION", {
      action: "delete",
      locationId: location_id,
    })

    // redirect to location route since the asset route isn't valid anymore
    void navigate(
      {
        pathname: ROUTES.LOCATION_DETAILS,
        search: stringifySearchParams({
          id: location_id,
          subtitle: locationSubtitle,
          title: locationTitle,
          tab: LOCATION_DETAILS_TABS[0].path,
        }),
      },
      { replace: true }
    )
  }, [saved, mutateAsync, navigate])

  if (!asset_id && !id) return

  return (
    <ActionMenuItem
      onClick={
        !saved
          ? () => {
              void createAsset()
            }
          : removeAsset
      }
    >
      {!saved ? (
        <VisibilityIcon sx={{ ...IconStyles }} />
      ) : (
        <VisibilityOffIcon sx={{ ...IconStyles }} />
      )}

      <Typography>
        {!saved ? "Start Watching Location" : "Stop Watching Location"}
      </Typography>
    </ActionMenuItem>
  )
}
